
import { useState } from "react";
import { StudentLayout } from "@/components/student/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, HelpCircle, Loader2, RefreshCw, User } from "lucide-react";
import { AccountTab } from "@/components/student/settings/AccountTab";
import { BillingTab } from "@/components/student/settings/BillingTab";
import { SupportTab } from "@/components/student/settings/SupportTab";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";

const StudentSettings = () => {
  const [activeTab, setActiveTab] = useState("account");
  const [switchingRole, setSwitchingRole] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['userProfile', userData?.id],
    queryFn: async () => {
      if (!userData?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userData.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userData?.id,
  });

  const handleSwitchToCreator = async () => {
    if (!userData?.id) return;
    
    try {
      setSwitchingRole(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'creator' })
        .eq('id', userData.id);
        
      if (error) throw error;
      
      await queryClient.invalidateQueries({
        queryKey: ['userProfile', userData.id],
      });
      
      toast.success("Switching to creator mode");
      
      setTimeout(() => {
        window.location.href = '/dashboard/creator';
      }, 500);
      
    } catch (error) {
      console.error("Error switching role:", error);
      toast.error("Failed to switch to creator mode");
      setSwitchingRole(false);
    }
  };

  if (userLoading || profileLoading) {
    return (
      <StudentLayout title="Settings" description="Manage your account settings and preferences">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout
      title="Settings"
      description="Manage your account settings and preferences"
    >
      <div className="container max-w-screen-lg mx-auto">
        {profile?.username && (
          <Alert className="mb-6">
            <AlertDescription className="flex items-center justify-between">
              <span>You are currently in student mode. Switch back to creator mode to manage your courses.</span>
              <Button
                onClick={handleSwitchToCreator}
                variant="outline"
                disabled={switchingRole}
                className="gap-2"
              >
                {switchingRole ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Switch to Creator Mode
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid grid-cols-3 mb-8">
            <TabsTrigger value="account" className="flex items-center gap-2">
              <User size={16} />
              <span>Account</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <CreditCard size={16} />
              <span>Billing</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <HelpCircle size={16} />
              <span>Support</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account">
            <AccountTab 
              userData={userData} 
              hideAccountRole={true}
            />
          </TabsContent>
          
          <TabsContent value="billing">
            <BillingTab />
          </TabsContent>
          
          <TabsContent value="support">
            <SupportTab />
          </TabsContent>
        </Tabs>
      </div>
    </StudentLayout>
  );
};

export default StudentSettings;
